<template>
  <div class="product-editor">
    <div class="product-editor__header">
      <v-btn icon class="product-editor__back" aria-label="Back" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="product-editor__title">
        <span>Products</span>
        <h2>{{ formTitle }}</h2>
      </div>
    </div>

    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      class="product-editor__form"
      @submit.prevent="handleSubmit(true)"
    >
      <v-expansion-panels v-model="openPanels" multiple class="product-sections" flat>
        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 1</span>
              <strong>Essentials</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="form-grid form-grid--two">
              <v-text-field
                v-model="name"
                label="Name"
                :rules="[v => !!v || 'Name is required']"
                outlined
                dense
                hide-details="auto"
              />

              <v-select
                :items="categories"
                item-text="name"
                v-model="category"
                item-value="id"
                return-object
                label="Category"
                required
                :rules="[v => !!v && v.id != null || 'Category is required']"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field v-model="sku" label="SKU" outlined dense hide-details="auto" />

              <v-select
                :items="suppliers"
                item-text="name"
                v-model="supplier"
                item-value="id"
                return-object
                label="Supplier"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field v-model="barcode" label="Barcode" outlined dense hide-details="auto" inputmode="numeric" />

              <v-text-field v-model="position" label="Position" type="number" outlined dense hide-details="auto" />

              <v-textarea
                v-model="description"
                label="Description"
                outlined
                dense
                auto-grow
                rows="2"
                hide-details="auto"
              />

              <v-textarea
                v-model="popupNotes"
                label="Popup notes"
                outlined
                dense
                auto-grow
                rows="2"
                hide-details="auto"
              />

              <v-text-field
                v-model="tags"
                label="Tags"
                outlined
                dense
                hide-details="auto"
                class="form-grid__wide"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 2</span>
              <strong>Pricing</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="section-toggle">
              <v-switch v-model="weightableItem" inset label="Weightable item" hide-details></v-switch>
            </div>

            <div class="form-grid form-grid--three">
              <v-text-field
                v-model="taxPercentage"
                label="Tax"
                type="number"
                suffix="%"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field
                v-if="weightableItem"
                v-model="grossWeight"
                label="Weight"
                type="number"
                suffix="grams"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field
                v-model="buyingPriceNoVAT"
                :label="buyingPriceLabel"
                type="number"
                inputmode="decimal"
                prefix="GBP"
                :rules="[
                  v => (v !== null && v !== '' && v !== undefined) || 'Buying Price is required',
                  v => /^-?[\d,]+(\.\d*)?$/.test(v) || 'Invalid number'
                ]"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field
                v-model="sellingPrice"
                label="Selling Price - incl VAT"
                type="number"
                inputmode="decimal"
                prefix="GBP"
                :rules="[
                  v => !!v || 'Selling Price is required',
                  v => /^-?[\d,]+(\.\d*)?$/.test(v) || 'Invalid number'
                ]"
                outlined
                dense
                hide-details="auto"
              />
            </div>

            <div class="summary-grid">
              <div class="summary-tile">
                <span>Buying incl VAT</span>
                <strong>GBP {{ buyingPrice || 0 }}</strong>
              </div>
              <div class="summary-tile">
                <span>{{ sellingPriceLabel }}</span>
                <strong>GBP {{ sellingPriceNoVAT || 0 }}</strong>
              </div>
              <div class="summary-tile">
                <span>Margin incl VAT</span>
                <strong>{{ margin }}</strong>
              </div>
              <div class="summary-tile">
                <span>Margin excl VAT</span>
                <strong>{{ marginNoVAT }}</strong>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 3</span>
              <strong>Stock</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="section-toggle">
              <v-switch v-model="isVolume" inset label="Volume item (e.g. spirits bottle sold by ml)" hide-details></v-switch>
            </div>

            <div class="form-grid form-grid--three">
              <v-text-field
                v-model="currentStock"
                :label="currentStockLabel"
                type="number"
                :rules="stockRules"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field
                v-model="minStock"
                :label="minStockLabel"
                type="number"
                :rules="stockRules"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field
                v-model="maxStock"
                :label="maxStockLabel"
                type="number"
                :rules="stockRules"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field
                v-if="isVolume"
                v-model="containerVolumeMl"
                label="Container size (ml)"
                type="number"
                inputmode="decimal"
                suffix="ml"
                outlined
                dense
                hide-details="auto"
              />

              <v-text-field v-model="boxedQty" label="Boxed Qty" type="number" outlined dense hide-details="auto" />

              <v-text-field
                v-if="+boxedQty > 0"
                v-model="boxedPrice"
                label="Boxed Price"
                type="number"
                inputmode="decimal"
                prefix="GBP"
                outlined
                dense
                hide-details="auto"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 4</span>
              <strong>Options</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="option-grid">
              <label class="option-card">
                <v-checkbox v-model="eposOnly" label="EPOS Only" hide-details></v-checkbox>
              </label>
              <label class="option-card">
                <v-checkbox v-model="hidden" label="Hidden From UI (EPOS)" hide-details></v-checkbox>
              </label>
              <label class="option-card">
                <v-checkbox v-model="multiStoreItem" label="Multi Store Item" hide-details></v-checkbox>
              </label>
              <label class="option-card">
                <v-checkbox v-model="hasAllergens" label="Has Allergens" hide-details></v-checkbox>
              </label>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 5</span>
              <strong>Sizes</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ProductSizes ref="sizes" :productSizes="sizes" :isVolume="isVolume" />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 6</span>
              <strong>Extras</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ProductExtras ref="extras" :productExtras="extras" />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="product-section">
          <v-expansion-panel-header>
            <div class="product-section__header">
              <span>Step 7</span>
              <strong>Image</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="image-uploader">
              <v-file-input
                label="Image"
                truncate-length="15"
                show-size
                accept="image/*"
                capture="environment"
                prepend-icon=""
                prepend-inner-icon="mdi-camera"
                v-model="image"
                outlined
                dense
                hide-details="auto"
                @change="upload(image)"
              ></v-file-input>

              <div v-if="imageBase64 != null" class="image-preview">
                <img :src="imageBase64" alt="Product preview" />
                <v-btn type="button" text color="error" @click="removeImage">
                  <v-icon left small>mdi-delete</v-icon>
                  Remove image
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="product-editor__save-spacer"></div>

      <div class="product-save-bar">
        <v-alert v-if="showSuccess" dense text type="success" class="product-save-bar__alert">
          Product saved. Ready for the next one.
        </v-alert>

        <div class="product-save-bar__actions">
          <v-btn type="button" text @click="goBack">Cancel</v-btn>
          <v-btn type="submit" :disabled="!valid || isSaving" :loading="isSaving" color="primary" depressed>
            Save
          </v-btn>
          <v-btn
            type="button"
            :disabled="!valid || isSaving"
            :loading="isSaving && saveMode === 'another'"
            color="primary"
            outlined
            @click="saveAnother()"
          >
            Save + Add Another
          </v-btn>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import { productService, categoryService, supplierService } from '@/services';
import ProductSizes from './ProductSizes';
import ProductExtras from './ProductExtras';

import Compressor from 'compressorjs';

export default {
  components: {
    ProductSizes,
    ProductExtras
  },
  data () {
    return {
      id: null,
      sku: '',
      name: '',
      description: '',
      buyingPriceNoVAT: '',
      sellingPrice: '',
      popupNotes: '',
      taxPercentage: 0,
      category: null,
      barcode: '',
      currentStock: '',
      minStock: '',
      maxStock: '',
      weightableItem: false,
      grossWeight: 0,
      isVolume: false,
      containerVolumeMl: 0,
      position: 0,
      supplier: null,

      categories: [],
      suppliers: [],
      sizes: [],
      extras: [],
      imageBase64: null,
      image: null,

      eposOnly: false,
      hidden: false,
      multiStoreItem: false,
      hasAllergens: false,
      boxedQty: 0,
      boxedPrice: null,
      tags: '',

      submitted: false,

      valid: true,
      showSuccess: false,
      isSaving: false,
      saveMode: 'save',
      openPanels: [0, 1, 2],
      stockRules: [
        v => (v !== null && v !== '' && v !== undefined) || 'Stock value is required',
        v => /^-?[\d,]+(\.\d*)?$/.test(v) || 'Stock value must be a valid number'
      ],
    }
  },
  async created () {
    await this.loadSuppliers();
    await this.loadCategories();

    var id = this.$route.params.id;
    if (id != null) {
      var dbProduct = await productService.getById(id);

      this.id = dbProduct.id;
      this.sku = dbProduct.sku;
      this.name = dbProduct.name;
      this.description = dbProduct.description;
      this.buyingPriceNoVAT = dbProduct.taxPercentage == 0 ? dbProduct.buyingPrice : (dbProduct.buyingPrice / (100 + dbProduct.taxPercentage) * 100).toFixed(8).replace(/\.?0+$/, '');
      this.taxPercentage = dbProduct.taxPercentage;
      this.sellingPrice = dbProduct.sellingPrice.toFixed(8).replace(/\.?0+$/, '');
      this.popupNotes = dbProduct.popupNotes;
      this.category = this.categories.find(c => c.id == dbProduct.category.id);
      this.barcode = dbProduct.barcode;
      this.currentStock = dbProduct.currentStock;
      this.minStock = dbProduct.minStock;
      this.maxStock = dbProduct.maxStock;
      this.weightableItem = dbProduct.weightableItem;
      this.grossWeight = dbProduct.grossWeight;
      this.isVolume = dbProduct.isVolume;
      this.containerVolumeMl = dbProduct.containerVolumeMl;
      this.position = dbProduct.position;
      if (dbProduct.supplierId != null)
        this.supplier = this.suppliers.find(s => s.id == dbProduct.supplierId);

      this.imageBase64 = dbProduct.imageBase64;

      this.sizes = dbProduct.sizes;
      this.extras = dbProduct.extras;
      this.eposOnly = dbProduct.eposOnly;
      this.hidden = dbProduct.hidden;
      this.multiStoreItem = dbProduct.multiStoreItem;
      this.hasAllergens = dbProduct.hasAllergens;
      this.boxedQty = dbProduct.boxedQty;
      this.boxedPrice = dbProduct.boxedPrice;
      this.tags = dbProduct.tags;
      this.openPanels = [0, 1, 2, 3, 4, 5, 6];
    }
  },
  methods: {
    goBack: function() {
      this.$router.go(-1);
    },
    loadSuppliers: async function() {
      this.suppliers = await supplierService.get();
    },
    loadCategories: async function() {
      var allCategories = await categoryService.get();
      allCategories = allCategories.sort((a, b) => a.name.localeCompare(b.name))
      var categories = [];

      for (let category of allCategories) {
        if (category.parentCategoryId == null ) {
          categories.push({ header: category.name, divider: true });
        }
        categories.push(category);
      }

      this.categories = categories;
      this.categories.unshift({
        id: null,
        name: 'Please select'
      });
    },
    async saveAnother () {
      await this.handleSubmit(false);
    },
    async handleSubmit (reset = true) {
      this.saveMode = reset ? 'save' : 'another';
      if (!this.$refs.form.validate()) {
        this.openPanels = [0, 1, 2, 3, 4, 5, 6];
        return;
      }

      this.submitted = true;
      this.isSaving = true;

      var dto = {
        id: this.id,
        sku: this.sku,
        name: this.name,
        description: this.description,

        buyingPrice: this.buyingPrice,
        sellingPrice: this.sellingPrice,
        popupNotes: this.popupNotes,
        taxPercentage: this.taxPercentage,
        categoryId: this.category.id,
        barcode: this.barcode,
        currentStock: this.currentStock,
        minStock: this.minStock,
        maxStock: this.maxStock,
        weightableItem: this.weightableItem,
        grossWeight: this.grossWeight,
        isVolume: this.isVolume,
        containerVolumeMl: +this.containerVolumeMl || 0,
        position: this.position,
        supplierId: this.supplier?.id,
        sizes: this.$refs.sizes != null ? this.$refs.sizes.getSizes() : [],
        extras: this.$refs.extras != null ? this.$refs.extras.getExtras() : [],
        eposOnly: this.eposOnly,
        hidden: this.hidden,
        imageBase64: this.imageBase64,
        multiStoreItem: this.multiStoreItem,
        hasAllergens: this.hasAllergens,
        boxedQty: this.boxedQty,
        boxedPrice: this.boxedPrice,
        tags: this.tags
      }

      try {
        var response = null;
        if (dto.id != null)
          response = await productService.update(dto);
        else
          response = await productService.add(dto);

        if (response.status != 200) {
          alert('Error saving');
          return;
        }

        if (!reset) {
          this.resetForNextProduct();
          this.showSuccess = true;
          return;
        }

        this.$router.go(-1);
      } finally {
        this.isSaving = false;
      }
    },
    resetForNextProduct() {
      this.id = null;
      this.sku = '';
      this.name = '';
      this.description = '';
      this.popupNotes = '';
      this.barcode = '';
      this.position = 0;
      this.imageBase64 = null;
      this.image = null;
      this.tags = '';
      this.openPanels = this.getOpenPanelsForNextProduct();

      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },
    getOpenPanelsForNextProduct() {
      const panels = [0, 1, 2];

      if (this.hasSelectedOptions()) {
        panels.push(3);
      }

      if (this.$refs.sizes && this.$refs.sizes.hasSizes()) {
        panels.push(4);
      }

      if (this.$refs.extras && this.$refs.extras.hasExtras()) {
        panels.push(5);
      }

      return panels;
    },
    hasSelectedOptions() {
      return this.eposOnly || this.hidden || this.multiStoreItem || this.hasAllergens;
    },
    upload: function(file) {
      if (!file) {
        return;
      }

      new Compressor(file, {
        quality: 0.6,
        maxWidth: 512,

        success: (result) => {
          let reader = new FileReader();
          reader.onload = () => {
            this.imageBase64 = reader.result;
          }
          reader.readAsDataURL(result);
        },
        error(err) {
          console.log(err.message);
        },
      });
    },
    removeImage() {
      this.image = null;
      this.imageBase64 = null;
    },
  },

  computed: {
    formTitle() {
      return this.id == null ? 'Add product' : 'Edit product';
    },

    buyingPrice() {
      if (this.taxPercentage == 0)
        return this.buyingPriceNoVAT;

      return (+this.buyingPriceNoVAT + (this.buyingPriceNoVAT / 100 * this.taxPercentage)).toFixed(8).replace(/\.?0+$/, '');
    },

    sellingPriceNoVAT() {
      if (this.taxPercentage == 0)
        return this.sellingPrice;

      return ((+this.sellingPrice / (100 + +this.taxPercentage)) * 100).toFixed(8).replace(/\.?0+$/, '');
    },

    marginNoVAT() {
      var percentage = 0;
      var profit = this.sellingPriceNoVAT - this.buyingPriceNoVAT;
      if (isNaN(profit))
        return 'GBP 0';

      if (profit > 0 && this.sellingPriceNoVAT > 0)
        percentage = profit / this.sellingPriceNoVAT * 100;

      return 'GBP ' + profit.toFixed(8).replace(/\.?0+$/, '') + ' (' + percentage.toFixed(0).replace(/\.?0+$/, '') + '%)';
    },

    margin() {
      var percentage = 0;
      var profit = this.sellingPrice - this.buyingPrice;
      if (isNaN(profit))
        return 'GBP 0';

      if (profit > 0 && this.sellingPrice > 0)
        percentage = profit / this.sellingPrice * 100;

      return 'GBP ' + profit.toFixed(8).replace(/\.?0+$/, '') + ' (' + percentage.toFixed(0) + '%)';
    },

    buyingPriceLabel() {
      if (this.weightableItem)
        return 'Buying Price per gram - excl VAT';

      return 'Buying Price - excl VAT';
    },

    sellingPriceLabel() {
      if (this.weightableItem)
        return 'Selling Price per gram - excl VAT';

      return 'Selling Price - excl VAT';
    },

    currentStockLabel() {
      if (this.weightableItem)
        return 'Current Stock (grams)';

      return 'Current Stock';
    },

    minStockLabel() {
      if (this.weightableItem)
        return 'Min Stock (grams)';

      return 'Min Stock';
    },

    maxStockLabel() {
      if (this.weightableItem)
        return 'Max Stock (grams)';

      return 'Max Stock';
    }
  }
}
</script>

<style lang="scss" scoped>
.product-editor {
  padding-bottom: 8px;
  width: 100%;
}

.product-editor__header {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.product-editor__back {
  background: #ffffff;
  border: 1px solid #dfe7ef;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.product-editor__title {
  min-width: 0;

  span {
    color: #64748b;
    display: block;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
  }

  h2 {
    color: #111827;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.15;
    margin: 2px 0 0;
  }
}

.product-editor__form {
  display: grid;
  gap: 12px;
  width: 100%;
}

.product-sections {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
}

.product-section {
  border: 1px solid #dfe7ef;
  border-radius: 8px !important;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  width: 100%;

  &::before {
    display: none;
  }
}

.product-section__header {
  display: grid;
  gap: 2px;

  span {
    color: #0f766e;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  strong {
    color: #111827;
    font-size: 16px;
  }
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.form-grid__wide {
  grid-column: 1 / -1;
}

.section-toggle {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  display: flex;
  margin-bottom: 12px;
  min-height: 58px;
  padding: 4px 12px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
    width: 100%;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }
}

.summary-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
  margin-top: 12px;
}

.summary-tile {
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  padding: 12px;

  span {
    color: #64748b;
    display: block;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    color: #111827;
    display: block;
    font-size: 16px;
    margin-top: 4px;
  }
}

.option-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
}

.option-card {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  min-height: 58px;
  padding: 0 12px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
    width: 100%;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }

  ::v-deep .v-label {
    line-height: 1.25;
  }
}

.image-uploader {
  display: grid;
  gap: 14px;
}

.image-preview {
  align-items: start;
  display: grid;
  gap: 10px;

  img {
    border: 1px solid #dfe7ef;
    border-radius: 8px;
    max-width: 220px;
    width: 100%;
  }
}

.product-editor__save-spacer {
  height: 96px;
}

.product-save-bar {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  bottom: 10px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  display: grid;
  gap: 8px;
  padding: 10px;
  position: sticky;
  z-index: 5;
}

.product-save-bar__alert {
  margin: 0;
}

.product-save-bar__actions {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
}

@media (min-width: 720px) {
  .product-editor__title h2 {
    font-size: 28px;
  }

  .form-grid--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .form-grid--three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .summary-grid,
  .option-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .product-save-bar__actions {
    align-items: center;
    grid-template-columns: auto minmax(180px, auto) minmax(220px, auto);
    justify-content: end;
  }
}

@media (min-width: 1180px) {
  .product-editor {
    max-width: none;
  }

  .summary-grid,
  .option-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
